import {
  intakePublic as intakePublicSchema,
  intakeSubmission as intakeSubmissionSchema,
  type IntakeFormRequest,
  type IntakePublic,
  type IntakeSubmission,
  type ChangePasswordRequest,
  consent as consentSchema,
  type Consent,
  costLine as costLineSchema,
  type CostLine,
  type CreateConsentRequest,
  type CreateCostLineRequest,
  type CreateDeadlineRequest,
  type CreateParticipantRequest,
  type CreateItemRequest,
  type AddSavedPlaceToTripRequest,
  type CreateSavedPlaceRequest,
  type CreateShareLinkRequest,
  type CreateSnippetRequest,
  type CopyDayRequest,
  dayOption as dayOptionSchema,
  type DayOption,
  type DuplicateTripRequest,
  type CreateTravelDocumentRequest,
  type CreateTripFromTemplateRequest,
  type CreateTripRequest,
  deadline as deadlineSchema,
  type Deadline,
  type ErrorCode,
  type FileCategory,
  type FileSensitivity,
  type GuidanceLevel,
  moneySummary as moneySummarySchema,
  type MoneySummary,
  parseConfirmationResponse as parseConfirmationResponseSchema,
  type ParseConfirmationRequest,
  type ParseConfirmationResponse,
  participant as participantSchema,
  type Participant,
  item as itemSchema,
  type Item,
  planDay as planDaySchema,
  type PlanDay,
  planLoose as planLooseSchema,
  type PlanLoose,
  type RecordableConsentPurpose,
  savedPlace as savedPlaceSchema,
  type SavedPlace,
  type SaveTripPlaceRequest,
  sensitiveNote as sensitiveNoteSchema,
  type SensitiveNote,
  snippet as snippetSchema,
  type Snippet,
  type InsertSnippetRequest,
  sessionResponse,
  type SessionUser,
  shareLink as shareLinkSchema,
  type ShareLink,
  sharePlan as sharePlanSchema,
  type SharePlan,
  shareTripFile as shareTripFileSchema,
  type ShareTripFile,
  travelDocument as travelDocumentSchema,
  type TravelDocument,
  trip as tripSchema,
  type Trip,
  tripDay as tripDaySchema,
  type TripDay,
  tripFile as tripFileSchema,
  type TripFile,
  type UpdateCostLineRequest,
  type UpdateDayRequest,
  type UpdateDeadlineRequest,
  type UpdateParticipantRequest,
  type UpdateSavedPlaceRequest,
  type UpdateSnippetRequest,
  type UpdateItemRequest,
  type UpdateTravelDocumentRequest,
  type UpdateTripRequest,
  type UpsertSensitiveNoteRequest,
} from '@itin/shared'
import { z } from 'zod'
import { STORED_AT_HEADER } from './offline.js'

/**
 * The only place this app talks to the server.
 *
 * Responses are parsed with the same Zod schemas the server validates against,
 * so a contract drifting between the two surfaces here as a loud failure rather
 * than as `undefined` rendering somewhere three components down.
 */

export class ApiError extends Error {
  readonly code: ErrorCode
  readonly fields: Record<string, string> | undefined

  constructor(code: ErrorCode, fields?: Record<string, string>) {
    super(code)
    this.name = 'ApiError'
    this.code = code
    this.fields = fields
  }
}

const errorBody = z.object({
  error: z.object({
    code: z.enum([
      'validation_failed',
      'unauthorized',
      'forbidden',
      'not_found',
      'conflict',
      'rate_limited',
      'consent_required',
      'internal',
    ]),
    fields: z.record(z.string(), z.string()).optional(),
  }),
})

/**
 * Turns a refused response into the typed error the UI renders through i18n.
 *
 * One function rather than the same six lines in each caller, because a body
 * that fails to parse must become `internal` and not an unhandled `SyntaxError`
 * — and getting that wrong in one of four places is exactly the kind of thing
 * nobody notices until a server returns HTML.
 */
async function refusal(response: Response): Promise<ApiError> {
  const parsed = errorBody.safeParse(await response.json().catch(() => null))
  return parsed.success
    ? new ApiError(parsed.data.error.code, parsed.data.error.fields)
    : new ApiError('internal')
}

async function request<T>(
  schema: z.ZodType<T>,
  path: string,
  init?: RequestInit & { json?: unknown },
): Promise<T> {
  const { json, ...rest } = init ?? {}
  const response = await fetch(`/api${path}`, {
    ...rest,
    // The session lives in a same-origin httpOnly cookie. In development Vite
    // proxies /api, so this is same-origin there too.
    credentials: 'same-origin',
    headers: {
      ...(json === undefined ? {} : { 'content-type': 'application/json' }),
      ...rest.headers,
    },
    ...(json === undefined ? {} : { body: JSON.stringify(json) }),
  })

  if (!response.ok) throw await refusal(response)

  if (response.status === 204) return undefined as T
  return schema.parse(await response.json())
}

/**
 * A response body, and whether it came off the network or off this device.
 *
 * `storedAt` is the instant the service worker took the copy, and is null for
 * anything fetched just now. It exists because a cached itinerary that does not
 * say it is cached is the worst thing this product could hand somebody: the
 * times look authoritative, the page looks live, and the flight moved an hour
 * ago. Carried as a wrapper rather than folded into `SharePlan` because it is
 * a fact about this delivery, not about the trip — the server knows nothing
 * about it and the contract should not grow a field the server cannot fill.
 */
export interface Copy<T> {
  value: T
  storedAt: string | null
}

/**
 * A read that keeps the one header saying where the body came from. Used by the
 * traveller's page and nowhere else, because the traveller's reads are the only
 * ones the service worker is allowed to store.
 */
async function requestCopy<T>(schema: z.ZodType<T>, path: string): Promise<Copy<T>> {
  const response = await fetch(`/api${path}`, { credentials: 'same-origin' })
  if (!response.ok) throw await refusal(response)

  return {
    value: schema.parse(await response.json()),
    storedAt: response.headers.get(STORED_AT_HEADER),
  }
}

/**
 * A multipart upload. Deliberately not routed through `request()`: the body
 * is a `FormData` the browser encodes itself, so setting a `content-type`
 * header by hand — which is what `request()` does for every other call — would
 * fight the boundary `fetch` picks and corrupt the body.
 */
async function requestMultipart<T>(schema: z.ZodType<T>, path: string, body: FormData): Promise<T> {
  const response = await fetch(`/api${path}`, { method: 'POST', credentials: 'same-origin', body })

  if (!response.ok) throw await refusal(response)
  return schema.parse(await response.json())
}

/**
 * A response carrying file bytes rather than JSON. The unlock `code` — when
 * there is one — travels in the POST body, never in the URL: a code in a query
 * string is a code sitting in server access logs and browser history, and §6
 * of the build spec is explicit that it must never be logged.
 */
async function requestBlob(path: string, code?: string): Promise<Blob> {
  const response = await fetch(`/api${path}`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ code }),
  })

  if (!response.ok) throw await refusal(response)
  return response.blob()
}

/** Triggers a browser save of a blob without ever navigating away from the page. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

export const api = {
  login: (email: string, password: string): Promise<{ user: SessionUser }> =>
    request(sessionResponse, '/auth/login', { method: 'POST', json: { email, password } }),

  logout: (): Promise<void> => request(z.void(), '/auth/logout', { method: 'POST' }),

  me: (): Promise<{ user: SessionUser }> => request(sessionResponse, '/auth/me'),

  setGuidanceLevel: (guidanceLevel: GuidanceLevel): Promise<{ user: SessionUser }> =>
    request(sessionResponse, '/auth/me/guidance', { method: 'PATCH', json: { guidanceLevel } }),

  changePassword: (input: ChangePasswordRequest): Promise<void> =>
    request(z.void(), '/auth/me/password', { method: 'POST', json: input }),

  listTrips: (): Promise<Trip[]> => request(z.array(tripSchema), '/trips'),

  getTrip: (id: string): Promise<Trip> => request(tripSchema, `/trips/${id}`),

  createTrip: (input: CreateTripRequest): Promise<Trip> =>
    request(tripSchema, '/trips', { method: 'POST', json: input }),

  duplicateTrip: (id: string, input: DuplicateTripRequest): Promise<Trip> =>
    request(tripSchema, `/trips/${id}/duplicate`, { method: 'POST', json: input }),

  /** The starting points she keeps. `listTrips` leaves these out. */
  listTripTemplates: (): Promise<Trip[]> => request(z.array(tripSchema), '/trips/templates'),

  createTripFromTemplate: (input: CreateTripFromTemplateRequest): Promise<Trip> =>
    request(tripSchema, '/trips/from-template', { method: 'POST', json: input }),

  listDayOptions: (tripId: string): Promise<DayOption[]> =>
    request(z.array(dayOptionSchema), `/trips/${tripId}/day-options`),

  copyDayFrom: (dayId: string, input: CopyDayRequest): Promise<PlanDay> =>
    request(planDaySchema, `/days/${dayId}/copy-from`, { method: 'POST', json: input }),

  listParticipants: (tripId: string): Promise<Participant[]> =>
    request(z.array(participantSchema), `/trips/${tripId}/participants`),

  createParticipant: (tripId: string, input: CreateParticipantRequest): Promise<Participant> =>
    request(participantSchema, `/trips/${tripId}/participants`, { method: 'POST', json: input }),

  updateParticipant: (id: string, input: UpdateParticipantRequest): Promise<Participant> =>
    request(participantSchema, `/participants/${id}`, { method: 'PATCH', json: input }),

  deleteParticipant: (id: string): Promise<void> =>
    request(z.void(), `/participants/${id}`, { method: 'DELETE' }),

  listConsents: (participantId: string): Promise<Consent[]> =>
    request(z.array(consentSchema), `/participants/${participantId}/consents`),

  recordConsent: (participantId: string, input: CreateConsentRequest): Promise<Consent> =>
    request(consentSchema, `/participants/${participantId}/consents`, {
      method: 'POST',
      json: input,
    }),

  withdrawConsent: (id: string): Promise<Consent> =>
    request(consentSchema, `/consents/${id}/withdraw`, { method: 'POST' }),

  listSensitiveNotes: (participantId: string): Promise<SensitiveNote[]> =>
    request(z.array(sensitiveNoteSchema), `/participants/${participantId}/sensitive-notes`),

  saveSensitiveNote: (
    participantId: string,
    purpose: RecordableConsentPurpose,
    input: UpsertSensitiveNoteRequest,
  ): Promise<SensitiveNote> =>
    request(sensitiveNoteSchema, `/participants/${participantId}/sensitive-notes/${purpose}`, {
      method: 'PUT',
      json: input,
    }),

  listShareLinks: (tripId: string): Promise<ShareLink[]> =>
    request(z.array(shareLinkSchema), `/trips/${tripId}/share-links`),

  createShareLink: (tripId: string, input: CreateShareLinkRequest): Promise<ShareLink> =>
    request(shareLinkSchema, `/trips/${tripId}/share-links`, { method: 'POST', json: input }),

  renameShareLink: (id: string, label: string | null): Promise<ShareLink> =>
    request(shareLinkSchema, `/share-links/${id}`, { method: 'PATCH', json: { label } }),

  revokeShareLink: (id: string): Promise<ShareLink> =>
    request(shareLinkSchema, `/share-links/${id}/revoke`, { method: 'POST' }),

  getSharePlan: (token: string): Promise<Copy<SharePlan>> =>
    requestCopy(sharePlanSchema, `/share/${encodeURIComponent(token)}`),

  updateTrip: (id: string, input: UpdateTripRequest): Promise<Trip> =>
    request(tripSchema, `/trips/${id}`, { method: 'PATCH', json: input }),

  getPlan: (tripId: string): Promise<{ days: PlanDay[]; loose: PlanLoose }> =>
    request(
      z.object({ days: z.array(planDaySchema), loose: planLooseSchema }),
      `/trips/${tripId}/plan`,
    ),

  syncDays: (tripId: string): Promise<TripDay[]> =>
    request(z.array(tripDaySchema), `/trips/${tripId}/days/sync`, { method: 'POST' }),

  updateDay: (id: string, input: UpdateDayRequest): Promise<TripDay> =>
    request(tripDaySchema, `/days/${id}`, { method: 'PATCH', json: input }),

  createItem: (tripId: string, input: CreateItemRequest): Promise<Item> =>
    request(itemSchema, `/trips/${tripId}/items`, { method: 'POST', json: input }),

  updateItem: (id: string, input: UpdateItemRequest): Promise<Item> =>
    request(itemSchema, `/items/${id}`, { method: 'PATCH', json: input }),

  deleteItem: (id: string): Promise<void> =>
    request(z.void(), `/items/${id}`, { method: 'DELETE' }),

  /**
   * Reads a pasted confirmation email into item fields. Saves nothing — what
   * comes back is a draft, and it only becomes an itinerary through
   * `createItem` below, once Ana has looked at it.
   */
  parseConfirmation: (
    tripId: string,
    input: ParseConfirmationRequest,
  ): Promise<ParseConfirmationResponse> =>
    request(parseConfirmationResponseSchema, `/trips/${tripId}/confirmations/parse`, {
      method: 'POST',
      json: input,
    }),

  getMoneySummary: (tripId: string): Promise<MoneySummary> =>
    request(moneySummarySchema, `/trips/${tripId}/money`),

  createDeadline: (tripId: string, input: CreateDeadlineRequest): Promise<Deadline> =>
    request(deadlineSchema, `/trips/${tripId}/deadlines`, { method: 'POST', json: input }),

  updateDeadline: (id: string, input: UpdateDeadlineRequest): Promise<Deadline> =>
    request(deadlineSchema, `/deadlines/${id}`, { method: 'PATCH', json: input }),

  deleteDeadline: (id: string): Promise<void> =>
    request(z.void(), `/deadlines/${id}`, { method: 'DELETE' }),

  createCostLine: (tripId: string, input: CreateCostLineRequest): Promise<CostLine> =>
    request(costLineSchema, `/trips/${tripId}/cost-lines`, { method: 'POST', json: input }),

  updateCostLine: (id: string, input: UpdateCostLineRequest): Promise<CostLine> =>
    request(costLineSchema, `/cost-lines/${id}`, { method: 'PATCH', json: input }),

  deleteCostLine: (id: string): Promise<void> =>
    request(z.void(), `/cost-lines/${id}`, { method: 'DELETE' }),
  listTravelDocumentsForTrip: (tripId: string): Promise<TravelDocument[]> =>
    request(z.array(travelDocumentSchema), `/trips/${tripId}/travel-documents`),

  listTravelDocumentsForParticipant: (participantId: string): Promise<TravelDocument[]> =>
    request(z.array(travelDocumentSchema), `/participants/${participantId}/travel-documents`),

  createTravelDocument: (
    participantId: string,
    input: CreateTravelDocumentRequest,
  ): Promise<TravelDocument> =>
    request(travelDocumentSchema, `/participants/${participantId}/travel-documents`, {
      method: 'POST',
      json: input,
    }),

  updateTravelDocument: (id: string, input: UpdateTravelDocumentRequest): Promise<TravelDocument> =>
    request(travelDocumentSchema, `/travel-documents/${id}`, { method: 'PATCH', json: input }),

  deleteTravelDocument: (id: string): Promise<void> =>
    request(z.void(), `/travel-documents/${id}`, { method: 'DELETE' }),
  listFiles: (tripId: string): Promise<TripFile[]> =>
    request(z.array(tripFileSchema), `/trips/${tripId}/files`),

  /**
   * Fields are appended to `form` BEFORE the file, and that order is load-
   * bearing rather than cosmetic: the server reads them off the multipart
   * stream as it arrives, and a field placed after the file part is a field
   * the upload route never sees.
   */
  uploadFile: (
    tripId: string,
    file: File,
    fields: { category: FileCategory; sensitivity: FileSensitivity; unlockCode?: string },
  ): Promise<TripFile> => {
    const form = new FormData()
    form.append('category', fields.category)
    form.append('sensitivity', fields.sensitivity)
    if (fields.unlockCode) form.append('unlockCode', fields.unlockCode)
    form.append('file', file)
    return requestMultipart(tripFileSchema, `/trips/${tripId}/files`, form)
  },

  listSavedPlaces: (): Promise<SavedPlace[]> =>
    request(z.array(savedPlaceSchema), '/saved-places'),

  createSavedPlace: (input: CreateSavedPlaceRequest): Promise<SavedPlace> =>
    request(savedPlaceSchema, '/saved-places', { method: 'POST', json: input }),

  updateSavedPlace: (id: string, input: UpdateSavedPlaceRequest): Promise<SavedPlace> =>
    request(savedPlaceSchema, `/saved-places/${id}`, { method: 'PATCH', json: input }),

  deleteSavedPlace: (id: string): Promise<void> =>
    request(z.void(), `/saved-places/${id}`, { method: 'DELETE' }),

  addSavedPlaceToTrip: (tripId: string, input: AddSavedPlaceToTripRequest): Promise<Item> =>
    request(itemSchema, `/trips/${tripId}/places/from-saved`, { method: 'POST', json: input }),

  saveTripPlace: (input: SaveTripPlaceRequest): Promise<SavedPlace> =>
    request(savedPlaceSchema, '/saved-places/from-trip', { method: 'POST', json: input }),

  getIntakePublic: (agencyId: string): Promise<IntakePublic> =>
    request(intakePublicSchema, `/intake/${agencyId}`),

  submitIntake: (agencyId: string, input: IntakeFormRequest): Promise<unknown> =>
    request(z.unknown(), `/intake/${agencyId}`, { method: 'POST', json: input }),

  listIntake: (): Promise<IntakeSubmission[]> =>
    request(z.array(intakeSubmissionSchema), '/intake-submissions'),

  markIntakeHandled: (id: string, handled: boolean): Promise<IntakeSubmission> =>
    request(intakeSubmissionSchema, `/intake-submissions/${id}`, { method: 'PATCH', json: { handled } }),

  listSnippets: (): Promise<Snippet[]> => request(z.array(snippetSchema), '/snippets'),

  createSnippet: (input: CreateSnippetRequest): Promise<Snippet> =>
    request(snippetSchema, '/snippets', { method: 'POST', json: input }),

  updateSnippet: (id: string, input: UpdateSnippetRequest): Promise<Snippet> =>
    request(snippetSchema, `/snippets/${id}`, { method: 'PATCH', json: input }),

  deleteSnippet: (id: string): Promise<void> =>
    request(z.void(), `/snippets/${id}`, { method: 'DELETE' }),

  /** Copies a text into a day's note. Returns the whole day, notes included. */
  insertSnippetIntoDay: (dayId: string, input: InsertSnippetRequest): Promise<TripDay> =>
    request(tripDaySchema, `/days/${dayId}/notes/from-snippet`, { method: 'POST', json: input }),

  deleteFile: (id: string): Promise<void> => request(z.void(), `/files/${id}`, { method: 'DELETE' }),

  downloadFile: (id: string, code?: string): Promise<Blob> => requestBlob(`/files/${id}/download`, code),

  listShareFiles: (token: string): Promise<ShareTripFile[]> =>
    request(z.array(shareTripFileSchema), `/share/${encodeURIComponent(token)}/files`),

  downloadShareFile: (token: string, fileId: string, code?: string): Promise<Blob> =>
    requestBlob(`/share/${encodeURIComponent(token)}/files/${fileId}/download`, code),
}
